// Horizontal-scroller keyboard + pointer support.
//
// Auto-initializes every `.js-horizontal-scroller` with a
// `.js-horizontal-scroller__track` child (the scrolling element). The track is
// the keyboard-accessible scroll region: focusable (tabindex toggled by
// overflow) and driven by ←/→ (prev/next card), PageUp/PageDown (page of
// cards), and Home/End. A `.horizontal-scroller__progress` bar is injected as a
// pointer-only visual affordance (click/drag to scrub), hidden when content
// fits without scrolling.
//
// Imported once from js/global.js; no-op if no scrollers are on the page.

let scrollerUid = 0;

// Tinted per scroller via currentColor (--scroller-thumb-color).
const PLANE_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="29" height="29" viewBox="0 0 29 29" fill="none" aria-hidden="true" focusable="false">' +
  '<path d="M27.1875 14.4868C27.1875 15.0088 27.022 15.48 26.6679 15.8667C26.3296 16.2376 25.8523 16.4914 25.2989 16.6714C24.2017 17.0267 22.5922 17.1548 20.4897 17.1548L18.7739 17.1548L14.1701 24.4132L14.1508 24.441C13.6276 25.1709 12.7987 25.3811 12.0833 25.3594L9.66667 25.3606C9.56791 25.3605 9.47067 25.3363 9.38345 25.29C9.29623 25.2437 9.22169 25.1767 9.16637 25.0949C9.11104 25.0131 9.07661 24.919 9.06608 24.8208C9.05556 24.7226 9.06926 24.6233 9.106 24.5316L12.2114 16.7911L7.01196 16.46L5.568 19.0023L5.55833 19.0216C5.27317 19.4844 4.74513 19.7189 4.25454 19.7189C4.21396 19.7189 4.17348 19.7148 4.13371 19.7068L2.29463 19.3286C2.21112 19.3111 2.13224 19.2762 2.06314 19.2261C1.99405 19.176 1.93629 19.112 1.89364 19.0381C1.85099 18.9642 1.82441 18.8821 1.81563 18.7973C1.80686 18.7124 1.81608 18.6266 1.84271 18.5456L3.19967 14.4856L1.84271 10.4413C1.81568 10.3602 1.80615 10.2744 1.81475 10.1894C1.82335 10.1044 1.84987 10.0221 1.89258 9.94814C1.93529 9.87414 1.99321 9.81005 2.06251 9.76008C2.13181 9.71011 2.21093 9.67541 2.29463 9.65827L4.13371 9.28006C4.17348 9.27198 4.21396 9.26794 4.25454 9.26798C4.74513 9.26798 5.27317 9.50239 5.55833 9.96639L5.568 9.98331L7.01196 12.5269L12.2102 12.1946L9.106 4.45519C9.06952 4.36358 9.05601 4.26444 9.06666 4.16641C9.0773 4.06838 9.11178 3.97445 9.16708 3.89281C9.22238 3.81118 9.29682 3.74432 9.3839 3.69806C9.47098 3.65181 9.56806 3.62758 9.66667 3.62748L12.0833 3.62748C12.7987 3.60573 13.6276 3.81598 14.1496 4.54581L14.1701 4.5736L18.7739 11.8321L20.4885 11.8321C22.5922 11.8321 24.2005 11.9577 25.2977 12.3094C25.8511 12.487 26.3272 12.7383 26.6667 13.1081C27.022 13.4947 27.1863 13.966 27.1875 14.4868Z" fill="currentColor"/>' +
  '</svg>';

// Fill shown at scroll 0 so the plane starts on a visible nub of track fill.
const MIN_FILL = 32;

export default function initHorizontalScrollers() {
  const containers = document.querySelectorAll(".js-horizontal-scroller");
  if (!containers.length) return;

  containers.forEach((container) => initOne(container));

  // Restaurants block swaps the track innerHTML on AJAX filter — re-init any
  // container that gets new content. Containers fire `scroller:refresh` on
  // themselves to ask us to recalc.
  document.addEventListener("scroller:refresh", (e) => {
    const target = e.target.closest && e.target.closest(".js-horizontal-scroller");
    if (target && target._scrollerUpdate) {
      target._scrollerUpdate();
    }
  });
}

function initOne(container) {
  const track = container.querySelector(".js-horizontal-scroller__track");
  if (!track) return;

  if (!track.id) {
    track.id = "horizontal-scroller-track-" + ++scrollerUid;
  }

  // Section-level so the bar centers on the full-width section (the screen),
  // not within each scroller's padded/offset container.
  const host = container.closest(".aviatrix-block") || container;

  let bar = host.querySelector(":scope > .horizontal-scroller__progress");
  let thumb;
  let plane;
  if (!bar) {
    bar = document.createElement("div");
    bar.className = "horizontal-scroller__progress";
    thumb = document.createElement("div");
    thumb.className = "horizontal-scroller__progress-thumb";
    thumb.setAttribute("aria-hidden", "true");
    bar.appendChild(thumb);
    host.appendChild(bar);
  } else {
    thumb = bar.querySelector(".horizontal-scroller__progress-thumb");
    if (thumb) thumb.setAttribute("aria-hidden", "true");
    plane = bar.querySelector(".horizontal-scroller__progress-plane");
  }
  if (!plane) {
    plane = document.createElement("span");
    plane.className = "horizontal-scroller__progress-plane";
    plane.setAttribute("aria-hidden", "true");
    plane.innerHTML = PLANE_SVG;
    bar.appendChild(plane);
  }

  // The track is the keyboard-accessible scroll region (tabindex toggled by
  // overflow in update()); name it so it announces meaningfully.
  if (!track.getAttribute("aria-label") && !track.getAttribute("aria-labelledby")) {
    track.setAttribute("aria-label", container.dataset.scrollerLabel || "Scrollable list");
  }

  // The bar is a pointer-only visual affordance; keyboard lives on the track.
  bar.setAttribute("aria-hidden", "true");

  function metrics() {
    const maxScroll = track.scrollWidth - track.clientWidth;
    const barWidth = bar.clientWidth;
    return { maxScroll, barWidth };
  }

  function update() {
    const m = metrics();

    if (m.maxScroll <= 1) {
      bar.style.visibility = "hidden";
      track.removeAttribute("tabindex");
      return;
    }
    bar.style.visibility = "";
    track.setAttribute("tabindex", "0");

    // Fill from the left like a progress bar; the plane rides the leading edge.
    const progress = Math.max(0, Math.min(1, track.scrollLeft / m.maxScroll));
    const fillWidth = MIN_FILL + progress * (m.barWidth - MIN_FILL);

    thumb.style.width = fillWidth + "px";
    plane.style.left = fillWidth + "px";
  }

  container._scrollerUpdate = update;
  update();
  track.addEventListener("scroll", update, { passive: true });
  window.addEventListener("resize", update, { passive: true });

  // -------------------------------------------------------------------------
  // Pointer interaction (mouse / touch / pen)
  // -------------------------------------------------------------------------
  let dragging = false;
  let dragPointerId = null;

  // Pointer x maps straight to the fill's leading edge (the plane).
  function scrollLeftForPointer(clientX) {
    const m = metrics();
    if (m.maxScroll <= 1 || m.barWidth <= MIN_FILL) return 0;
    const x = clientX - bar.getBoundingClientRect().left;
    const progress = Math.max(0, Math.min(1, (x - MIN_FILL) / (m.barWidth - MIN_FILL)));
    return progress * m.maxScroll;
  }

  bar.addEventListener("pointerdown", (e) => {
    if (bar.style.visibility === "hidden") return;
    e.preventDefault();

    track.scrollLeft = scrollLeftForPointer(e.clientX);

    dragging = true;
    dragPointerId = e.pointerId;
    try {
      bar.setPointerCapture(e.pointerId);
    } catch (_) {}
    bar.classList.add("is-dragging");
  });

  bar.addEventListener("pointermove", (e) => {
    if (!dragging || e.pointerId !== dragPointerId) return;
    track.scrollLeft = scrollLeftForPointer(e.clientX);
  });

  function endDrag(e) {
    if (!dragging || (e && e.pointerId !== dragPointerId)) return;
    dragging = false;
    try {
      bar.releasePointerCapture(dragPointerId);
    } catch (_) {}
    dragPointerId = null;
    bar.classList.remove("is-dragging");
  }
  bar.addEventListener("pointerup", endDrag);
  bar.addEventListener("pointercancel", endDrag);

  // -------------------------------------------------------------------------
  // Keyboard interaction (only when the track itself is focused — card links
  // keep their native behavior). Steps by whole cards so `scroll-snap-type:
  // mandatory` lands on a snap point; a sub-card pixel step snaps straight back.
  // -------------------------------------------------------------------------
  function currentIndex() {
    const left = track.getBoundingClientRect().left;
    let idx = 0;
    for (let i = 0; i < track.children.length; i++) {
      if (track.children[i].getBoundingClientRect().left <= left + 1) idx = i;
    }
    return idx;
  }

  function scrollByCards(count) {
    const items = track.children;
    if (!items.length) return;
    const target = Math.max(0, Math.min(items.length - 1, currentIndex() + count));
    items[target].scrollIntoView({ inline: "start", block: "nearest", behavior: "smooth" });
  }

  function visibleCards() {
    const first = track.children[0];
    if (!first) return 1;
    return Math.max(1, Math.floor(track.clientWidth / first.getBoundingClientRect().width));
  }

  track.addEventListener("keydown", (e) => {
    if (e.target !== track || bar.style.visibility === "hidden") return;
    let handled = true;
    switch (e.key) {
      case "ArrowLeft":
      case "ArrowUp":
        scrollByCards(-1);
        break;
      case "ArrowRight":
      case "ArrowDown":
        scrollByCards(1);
        break;
      case "PageUp":
        scrollByCards(-visibleCards());
        break;
      case "PageDown":
        scrollByCards(visibleCards());
        break;
      case "Home":
        track.scrollTo({ left: 0, behavior: "smooth" });
        break;
      case "End":
        track.scrollTo({ left: track.scrollWidth, behavior: "smooth" });
        break;
      default:
        handled = false;
    }
    if (handled) e.preventDefault();
  });
}
